import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth API
export const authAPI = {
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  getMe: () => apiClient.get('/auth/me'),
};

// Organization API
export const orgAPI = {
  list: () => apiClient.get('/organizations'),
  get: (id) => apiClient.get(`/organizations/${id}`),
  create: (data) => apiClient.post('/organizations', data),
};

// Lesson Plan API
export const planAPI = {
  list: (orgId, myPlans = false) => apiClient.get(`/org/${orgId}/lesson-plans`, { params: { my_plans: myPlans } }),
  create: (orgId, data) => apiClient.post(`/org/${orgId}/lesson-plans`, data),
  update: (orgId, planId, data) => apiClient.put(`/org/${orgId}/lesson-plans/${planId}`, data),
  delete: (orgId, planId) => apiClient.delete(`/org/${orgId}/lesson-plans/${planId}`),
  aiGenerate: (orgId, data) => apiClient.post(`/org/${orgId}/lesson-plans/ai-generate`, data),
};

// Community API
export const communityAPI = {
  listPosts: (limit = 20) => apiClient.get('/community/posts', { params: { limit } }),
  createPost: (data) => apiClient.post('/community/posts', data),
  createComment: (postId, data) => apiClient.post(`/community/posts/${postId}/comments`, data),
  listComments: (postId) => apiClient.get(`/community/posts/${postId}/comments`),
  likePost: (postId) => apiClient.post(`/community/posts/${postId}/like`),
  getPostLikes: (postId) => apiClient.get(`/community/posts/${postId}/likes`),
};

// Learning API
export const learningAPI = {
  listCourses: () => apiClient.get('/learning/courses'),
  getCourse: (courseId) => apiClient.get(`/learning/courses/${courseId}`),
  getCourseLessons: (courseId) => apiClient.get(`/learning/courses/${courseId}/lessons`),
  getLesson: (lessonId) => apiClient.get(`/learning/lessons/${lessonId}`),
  // Enrollment & Progress
  getMyEnrollments: () => apiClient.get('/learning/my-enrollments'),
  getMyEnrollmentsDetailed: () => apiClient.get('/learning/my-enrollments/detailed'),
  enrollCourse: (courseId) => apiClient.post(`/learning/courses/${courseId}/enroll`),
  getCourseProgress: (courseId) => apiClient.get(`/learning/courses/${courseId}/progress`),
  markLessonComplete: (lessonId) => apiClient.post(`/learning/lessons/${lessonId}/complete`),
  unmarkLessonComplete: (lessonId) => apiClient.post(`/learning/lessons/${lessonId}/uncomplete`),
};

// Accreditation API
export const accreditationAPI = {
  listCriteria: () => apiClient.get('/accreditation/criteria'),
  listEvidence: (orgId) => apiClient.get(`/org/${orgId}/accreditation/evidence`),
  createEvidence: (orgId, data) => apiClient.post(`/org/${orgId}/accreditation/evidence`, data),
  updateEvidence: (orgId, evidenceId, data) => apiClient.put(`/org/${orgId}/accreditation/evidence/${evidenceId}`, data),
  listActionPlans: (orgId) => apiClient.get(`/org/${orgId}/action-plans`),
  createActionPlan: (orgId, data) => apiClient.post(`/org/${orgId}/action-plans`, data),
  updateActionPlan: (orgId, actionId, data) => apiClient.put(`/org/${orgId}/action-plans/${actionId}`, data),
  deleteActionPlan: (orgId, actionId) => apiClient.delete(`/org/${orgId}/action-plans/${actionId}`),
};

// Admin API
export const adminAPI = {
  listUsers: () => apiClient.get('/auth/admin/users'),
  createUser: (data) => apiClient.post('/auth/admin/users', data),
  updateUser: (userId, data) => apiClient.put(`/auth/admin/users/${userId}`, data),
  changePassword: (userId, newPassword) => apiClient.put(`/auth/admin/users/${userId}/password`, { newPassword }),
  deleteUser: (userId) => apiClient.delete(`/auth/admin/users/${userId}`),
  getUserRoles: (userId) => apiClient.get(`/auth/admin/users/${userId}/roles`),
  assignRole: (userId, data) => apiClient.post(`/auth/admin/users/${userId}/roles`, data),
  removeRole: (userId, roleId) => apiClient.delete(`/auth/admin/users/${userId}/roles/${roleId}`),
  updateOrg: (orgId, data) => apiClient.put(`/admin/organizations/${orgId}`, data),
  deleteOrg: (orgId) => apiClient.delete(`/admin/organizations/${orgId}`),
  updateCourse: (courseId, data) => apiClient.put(`/admin/courses/${courseId}`, data),
  deleteCourse: (courseId) => apiClient.delete(`/admin/courses/${courseId}`),
  createLesson: (courseId, data) => apiClient.post(`/admin/courses/${courseId}/lessons`, data),
  updateLesson: (lessonId, data) => apiClient.put(`/admin/lessons/${lessonId}`, data),
  deleteLesson: (lessonId) => apiClient.delete(`/admin/lessons/${lessonId}`),
};

// Stats API
export const statsAPI = {
  network: () => apiClient.get('/stats/network'),
  org: (orgId) => apiClient.get(`/stats/org/${orgId}`),
};
